import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import { IMG, resolveImage } from "../data/content";

const NOTES = [
  { title: "The tents", text: "Only five tents across ten acres, each with an attached washroom and running water. Space is the luxury here." },
  { title: "Light & power", text: "There is no conventional electricity or internet. Solar lamps and an inverter light the nights; batteries charge when the sun allows." },
  { title: "Connectivity", text: "Phone network is limited to BSNL in certain spots only. Everything else is birdsong." },
  { title: "Food & drink", text: "Mostly grown in and around the lodge and served fresh. Tell us about allergies or dietary needs in advance. No alcohol is stocked at the lodge." },
  { title: "Fitness", text: "The lodge is reached on foot, with some incline. Basic fitness is essential; guests between 12 and 60 years are recommended." },
  { title: "What to pack", text: "Jungle colours, trekking shoes, flashlight, water bottle, insect repellent, battery packs, binoculars, camera — and something to read or write." },
];

export default function StayPage() {
  return (
    <main data-testid="stay-page">
      <Seo
        title="Stay at The Jamoon | Wilderness Lodge in Corbett"
        description="Stay at The Jamoon, a secluded wilderness lodge beside the Ramganga in Corbett. Discover forest, river, nature and an experience designed around disconnection."
        path="/stay"
        image={IMG.lodgePano}
        imageAlt="Interior of a tent at The Jamoon wilderness lodge in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Stay", path: "/stay" }]}
      />
      <PageHero
        testid="stay-hero"
        image={IMG.lodgePano}
        imageAlt="Interior of a tent at The Jamoon wilderness lodge in Corbett"
        eyebrow="Stay"
        title={<>Stay at <span className="italic text-brass">The Jamoon</span></>}
        sub="Comfortable, canvas-walled and quiet — a lodge that keeps out of the forest's way."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {NOTES.map((n, i) => (
            <Reveal key={n.title} delay={(i % 3) * 0.1}>
              <div data-testid={`stay-note-${i}`} className="border border-brass/20 p-8 transition-colors duration-500 hover:border-brass/50">
                <h2 className="font-serif text-2xl font-light text-ivory">{n.title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-ivory/60">{n.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-brass/15">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 sm:px-12 lg:grid-cols-2 lg:px-20">
          <Reveal className="group relative overflow-hidden">
            <img
              src={resolveImage("lodgeNight", 900, 1100)}
              alt="The Jamoon lodge glowing by lamplight at night"
              loading="lazy"
              className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
          </Reveal>
          <div>
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">At Night</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-4xl">
                Lit by lamps, <span className="italic text-brass">ruled by stars.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-ivory/75">
                With no electric glow for miles, darkness here is complete — and the sky answers
                it. Dinner ends by the fire; the walk back to your tent is by lantern, with the
                night shift of the forest already at work around you.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <FinalCta />
    </main>
  );
}
